/**
 * ASCII Art Generator - 抖动算法
 *
 * 包含 Floyd-Steinberg, Bayer, Atkinson 抖动算法
 */

import type { DitherType } from '../types'

// ============================================
// Bayer 矩阵
// ============================================

/** 4x4 Bayer矩阵 (归一化到0-1) */
const BAYER_4X4 = [
  [0/16, 8/16, 2/16, 10/16],
  [12/16, 4/16, 14/16, 6/16],
  [3/16, 11/16, 1/16, 9/16],
  [15/16, 7/16, 13/16, 5/16],
]

// ============================================
// Floyd-Steinberg 抖动
// ============================================

/**
 * Floyd-Steinberg 误差扩散抖动
 * 误差分布:
 *        X   7/16
 *   3/16 5/16 1/16
 */
export function floydSteinbergDither(
  pixels: Float32Array,
  width: number,
  height: number,
  levels: number = 2
): Float32Array {
  const result = new Float32Array(pixels)
  const step = 255 / (levels - 1)

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x
      const oldPixel = result[idx]
      const newPixel = Math.round(Math.round(oldPixel / step) * step)
      const error = oldPixel - newPixel

      result[idx] = newPixel

      // 扩散误差到邻近像素
      if (x + 1 < width) {
        result[idx + 1] += error * 7 / 16
      }
      if (y + 1 < height) {
        if (x > 0) {
          result[idx + width - 1] += error * 3 / 16
        }
        result[idx + width] += error * 5 / 16
        if (x + 1 < width) {
          result[idx + width + 1] += error * 1 / 16
        }
      }
    }
  }

  return result
}

// ============================================
// Atkinson 抖动
// ============================================

/**
 * Atkinson 抖动 (Apple风格)
 * 误差分布:
 *        X   1/8 1/8
 *   1/8 1/8 1/8
 *       1/8
 */
export function atkinsonDither(
  pixels: Float32Array,
  width: number,
  height: number,
  levels: number = 2
): Float32Array {
  const result = new Float32Array(pixels)
  const step = 255 / (levels - 1)

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x
      const oldPixel = result[idx]
      const newPixel = Math.round(Math.round(oldPixel / step) * step)
      const error = (oldPixel - newPixel) / 8 // 分布3/4的误差

      result[idx] = newPixel

      // Atkinson 误差扩散
      if (x + 1 < width) {
        result[idx + 1] += error
      }
      if (x + 2 < width) {
        result[idx + 2] += error
      }
      if (y + 1 < height) {
        if (x > 0) {
          result[idx + width - 1] += error
        }
        result[idx + width] += error
        if (x + 1 < width) {
          result[idx + width + 1] += error
        }
      }
      if (y + 2 < height) {
        result[idx + 2 * width] += error
      }
    }
  }

  return result
}

// ============================================
// Bayer 有序抖动
// ============================================

/**
 * Bayer 有序抖动
 */
export function bayerDither(
  pixels: Float32Array,
  width: number,
  height: number,
  levels: number = 2
): Float32Array {
  const result = new Float32Array(width * height)
  const step = 255 / (levels - 1)

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x
      const threshold = BAYER_4X4[y % 4][x % 4] * 255
      const pixel = pixels[idx]

      // 添加Bayer阈值
      const adjusted = pixel + (threshold - 127.5)
      result[idx] = Math.max(0, Math.min(255, Math.round(Math.round(adjusted / step) * step)))
    }
  }

  return result
}

// ============================================
// 辅助函数
// ============================================

/**
 * 将ImageData转换为灰度数组，可选应用对比度和亮度
 */
export function imageDataToGrayscaleArray(
  imageData: ImageData,
  contrast?: number,
  brightness?: number
): Float32Array {
  const { width, height, data } = imageData
  const result = new Float32Array(width * height)

  for (let i = 0; i < width * height; i++) {
    const idx = i * 4
    let gray = Math.round(0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2])

    // 应用对比度和亮度（如果提供）
    if (contrast !== undefined && contrast !== 1) {
      gray = Math.max(0, Math.min(255, Math.round((gray - 128) * contrast + 128)))
    }
    if (brightness !== undefined && brightness !== 0) {
      gray = Math.max(0, Math.min(255, gray + Math.round(brightness * 255)))
    }

    result[i] = gray
  }

  return result
}

/**
 * 应用量化到指定级别
 */
export function quantizeBrightness(brightness: number, levels: number): number {
  const step = 255 / (levels - 1)
  return Math.round(Math.round(brightness / step) * step)
}

/**
 * 应用抖动算法
 */
export function applyDither(
  grayscaleArray: Float32Array,
  width: number,
  height: number,
  ditherType: DitherType,
  levels: number = 2,
  strength: number = 1
): Float32Array {
  // 应用强度系数
  const adjusted = new Float32Array(grayscaleArray.length)
  for (let i = 0; i < grayscaleArray.length; i++) {
    adjusted[i] = grayscaleArray[i] * strength
  }

  switch (ditherType) {
    case 'floyd-steinberg':
      return floydSteinbergDither(adjusted, width, height, levels)
    case 'atkinson':
      return atkinsonDither(adjusted, width, height, levels)
    case 'bayer':
      return bayerDither(adjusted, width, height, levels)
    case 'none':
    default:
      return adjusted
  }
}